import { faArrowUpRightFromSquare, faStar } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IAPIResponse, StatusCode } from "api/protocols";
import { GetLoyaltyProgramCustomerTransactions, ILoyaltyProgramTransactionExtended } from "api/rpc/2024-04/customer/loyaltyProgram/loyaltyProgram";
import axios, { CancelToken } from "axios";
import { Badge } from "components/badge/Badge";
import Card from "components/card/Card";
import DataTable from "components/dataTable/DataTable";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { showError } from "redux/actions/ui";
import { ILoyaltyCustomer } from "redux/reducers/models/loyalty";
import "../portalLoyaltyProgram.scss";
import { GetLoyaltyCustomerTransactions } from "api/rpc/2024-04/facilityAdmin/customer/loyaltyCustomer";
import classNames from "classnames";

interface IProps {
  customer: ILoyaltyCustomer;
}

const TABLE_LIMIT = 10;
const DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";

export default function LoyaltyProgramRecentActivity(props: IProps) {
  const { customer } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [offset, setOffset] = useState<number>(0);
  const [transactions, setTransactions] = useState<Array<ILoyaltyProgramTransactionExtended>>(undefined);
  const { facilityStore } = useAppSelector(store => store);
  const facilityTransactions = !!facilityStore?.loyaltyProgram;

  useEffect(() => {
    const source = axios.CancelToken.source();
    void getTransactions(source.token);
    return () => source.cancel();
  }, [offset]);

  async function getTransactions(cancelToken: CancelToken) {
    if (transactions !== undefined) {
      setTransactions(undefined);
    }

    let res: IAPIResponse<ILoyaltyProgramTransactionExtended[]>;

    if (facilityTransactions) {
      res = await GetLoyaltyCustomerTransactions(
        { customer_id: customer?.customer_id, limit: TABLE_LIMIT, offset: offset },
        false,
        cancelToken,
      );
    } else {
      res = await GetLoyaltyProgramCustomerTransactions(
        { loyalty_program_id: customer?.loyalty_program_id, limit: TABLE_LIMIT, offset: offset },
        false,
        cancelToken,
      );
    }

    if (res?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      setTransactions([]);
      dispatch(showError(t("Error getting recent activity")));
      return;
    }

    setTransactions(res?.data);
  }

  function badgeType(kind: "credit" | "debit", amount: number) {
    if (kind === "credit") {
      return (
        <Badge type="success" size="small">
          <FontAwesomeIcon icon={faStar} className="mr-2" /> + {amount}
        </Badge>
      );
    }

    return (
      <Badge type="error" size="small">
        <FontAwesomeIcon icon={faStar} className="mr-2" /> - {amount}
      </Badge>
    );
  }

  function getSalesChannel(channel: string) {
    const salesChannel = channel ? channel?.toLocaleLowerCase() : "--";

    switch (salesChannel) {
      case "pos":
        return t("In Store");
      case "booking_engine":
        return t("Online Booking");
      case "online_store":
        return t("Online");
      default:
        return "--";
    }
  }

  function onOrderClick(orderId: number) {
    window.open(`/admin/order/${orderId}`);
  }

  function getActivityLabel(offersType: ILoyaltyProgramTransactionExtended["loyalty_program_offers_type"]) {
    switch (offersType) {
      case "welcome_bonus":
        return t("Welcome Bonus");
      case "birthday_bonus":
        return t("Birthday");
      case "points_adjustment":
        return t("Points Adjustment");
      case "standalone_import":
        return t("Import");
      default:
        return null;
    }
  }

  function renderActivity(transaction: ILoyaltyProgramTransactionExtended) {
    const activityLabel = getActivityLabel(transaction?.loyalty_program_offers_type);

    if (activityLabel) {
      return <p className="loyalty-program-activity-primary">{activityLabel}</p>;
    }

    const validOrderLink = facilityTransactions && transaction?.source_order_id;

    return <>
      <p
        onClick={validOrderLink ? () => onOrderClick(transaction?.source_order_id) : undefined}
        className={classNames("loyalty-program-activity-primary", {
          "loyalty-program-activity-link": validOrderLink,
        })}
      >
        {t("Order")} #{transaction?.order_number}{" "}
        {validOrderLink ? <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> : null}
      </p>
      <p>{getSalesChannel(transaction?.order_sales_channel)}</p>
    </>;
  }

  function renderExpiry(expiryDate: string) {
    if (!expiryDate) {
      return null;
    }

    const expiry = moment(expiryDate, DATE_FORMAT);

    if (expiry.isAfter(moment())) {
      return `${t("Exp")}. ${expiry.format("LL")}`;
    }

    if (expiry.isBefore(moment())) {
      return t("Expired");
    }

    return null;
  }

  return (
    <Card title={t("Recent Activity")}>
      <Card.Section table="true">
        <DataTable
          loading={!transactions}
          columns={[
            { label: t("Date") },
            { label: t("Activity") },
            { label: t("Points") },
            { label: t("Running Balance") },
          ]}
          footer={{
            tableLimit: TABLE_LIMIT,
            tableOffset: offset,
            disableNextOffset: !transactions || !(transactions?.length === TABLE_LIMIT),
            handleTableOffset: direction =>
              setOffset(prev => (direction === "prev" ? prev - TABLE_LIMIT : prev + TABLE_LIMIT)),
          }}
        >
          {transactions?.map((transaction, index) => (
            <tr key={index}>
              <td>
                <p className="loyalty-program-activity-primary">
                  {moment(transaction?.created_at, DATE_FORMAT)?.format("LL")}
                </p>
                <p>{transaction?.facility_long_name}</p>
              </td>
              <td>{renderActivity(transaction)}</td>
              <td>
                <p className="loyalty-program-activity-expiry">
                  {badgeType(transaction?.kind, transaction?.amount)}{" "}
                  {renderExpiry(transaction?.expiry_date)}
                </p>
              </td>
              <td>{transaction?.new_balance}</td>
            </tr>
          ))}
        </DataTable>
      </Card.Section>
    </Card>
  );
}
